import { StyledDataTable } from "components/molecules/StyledDataTable";
import { useEffect, useState } from "react";
import { SearchBar } from "components/molecules/SearchBar";
import { FlexExpandingWrapper } from "./styles";
import { accordionData } from "../Sidebar/accordion-data";
import { ActivityLibraryDataTableProps, DataRow } from "./types";
import { ACTIVITY_LIBRARY_COLUMNS } from "./columns";
import { hasCategory } from "../Sidebar/utils";
import {
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  Stack,
  Autocomplete,
  TextField,
  Chip,
  Box,
} from "@mui/material";
import { ACTIVITY_STATUS, StatusType } from "config";
import { Loader } from "pages/UserDetails/styles";
import { useDifficultyEnums } from "hooks/useDifficultyEnums";

export const ActivityLibraryDataTable = ({
  categoryFilter,
  loading,
  error,
  tableData,
  onRowClick,
  setActivityFilterStatus,
  activityFilterStatus,
  difficultyFilter,
  setDifficultyFilter,
}: ActivityLibraryDataTableProps): JSX.Element => {
  const [filterText, setFilterText] = useState<string>("");
  const [resetPaginationToggle, setResetPaginationToggle] =
    useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);

  const { difficultyEnums } = useDifficultyEnums();

  useEffect(() => {
    setShowError(!!error);
  }, [error]);

  useEffect(() => {
    setResetPaginationToggle((prev) => !prev);
  }, [difficultyFilter]);

  useEffect(() => {
    if (categoryFilter) {
      const shouldDisableDifficulty =
        isInstructionOrTransitionCategory(categoryFilter);
      if (shouldDisableDifficulty) {
        setDifficultyFilter?.([]);
      }
    }
  }, [categoryFilter, setDifficultyFilter]);

  const isInstructionOrTransitionCategory = (category: string) => {
    if (
      category.toLowerCase() === "instructions" ||
      category.toLowerCase() === "transition"
    ) {
      return true;
    }

    const instructionsContent = [
      "Intro",
      "Outro",
      "Recording Feedback",
      "Client Reflection",
    ];
    const transitionContent = ["Transition"];

    return (
      instructionsContent.includes(category) ||
      transitionContent.includes(category)
    );
  };

  const filteredItems: DataRow[] =
    tableData

      ?.filter((activity) =>
        activity?.allFields?.includes(filterText.toLowerCase())
      )

      ?.filter((activity) => {
        if (!categoryFilter) return true;

        if (
          hasCategory(categoryFilter, accordionData, activity?.cognitiveDomains)
        ) {
          return true;
        }

        return activity?.cognitiveDomains?.includes(categoryFilter as any);
      })

      ?.filter(
        (activity) =>
          !difficultyFilter ||
          difficultyFilter.length === 0 ||
          (activity.difficulty &&
            difficultyFilter.includes(activity.difficulty))
      ) ?? [];

  const handleClear = () => {
    if (filterText) {
      setResetPaginationToggle(!resetPaginationToggle);
      setFilterText("");
    }
  };

  const handleFilterStatusChange = (event: SelectChangeEvent<string>) => {
    setActivityFilterStatus(event.target.value);
  };

  if (loading) {
    return (
      <FlexExpandingWrapper>
        <Loader>
          <CircularProgress />
        </Loader>
      </FlexExpandingWrapper>
    );
  }

  return (
    <FlexExpandingWrapper>
      <StyledDataTable
        columns={ACTIVITY_LIBRARY_COLUMNS}
        data={filteredItems}
        pagination
        paginationPerPage={20}
        onRowClicked={onRowClick}
        subHeader
        subHeaderComponent={
          <Stack direction="row" spacing={2} alignItems={"center"} pb={"1rem"}>
            <Box sx={{ width: 180, flexShrink: 0 }}>
              <Select
                value={activityFilterStatus}
                onChange={handleFilterStatusChange}
                size="small"
              >
                {ACTIVITY_STATUS.map(
                  (activityStatus: StatusType, index: number) => (
                    <MenuItem key={index} value={activityStatus.value}>
                      Status: {activityStatus.name}
                    </MenuItem>
                  )
                )}
              </Select>
            </Box>
            <Autocomplete
              multiple
              options={difficultyEnums}
              value={difficultyFilter || []}
              onChange={(_, newValue) => setDifficultyFilter?.(newValue)}
              openOnFocus
              disabled={
                categoryFilter
                  ? isInstructionOrTransitionCategory(categoryFilter)
                  : false
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Difficulty"
                  placeholder={
                    difficultyFilter && difficultyFilter.length > 0
                      ? ""
                      : "Select"
                  }
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
              sx={{
                width:
                  difficultyFilter && difficultyFilter.length > 0
                    ? "fit-content"
                    : 150,
                minWidth:
                  difficultyFilter && difficultyFilter.length > 0
                    ? "fit-content"
                    : 150,
                maxWidth:
                  difficultyFilter && difficultyFilter.length > 0
                    ? "fit-content"
                    : 150,
                "& .MuiInputLabel-root": {
                  display: "flex",
                  alignItems: "center",
                  transform:
                    difficultyFilter && difficultyFilter.length > 0
                      ? "translate(14px, -9px) scale(0.75)"
                      : "translate(14px, 12px) scale(1)",
                  "&.Mui-focused": {
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                },
                "& .MuiAutocomplete-inputRoot": {
                  display: "flex",
                  flexWrap: "nowrap",
                  overflow: "hidden",
                  alignItems: "center",
                  paddingRight: "32px",
                  gap: "4px",
                  "& .MuiAutocomplete-input": {
                    minWidth:
                      difficultyFilter && difficultyFilter.length > 0
                        ? "0"
                        : "0",
                    width:
                      difficultyFilter && difficultyFilter.length > 0
                        ? "0 !important"
                        : "0",
                    padding: "0",
                    flexShrink: 0,
                    flexGrow: 0,
                  },
                  "& .MuiAutocomplete-endAdornment": {
                    position: "absolute",
                    right: "9px",
                  },
                  "& .MuiChip-root": {
                    height: "24px",
                    maxWidth: "100px",
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  },
                },
              }}
              limitTags={3}
              disableCloseOnSelect
              ListboxProps={{
                style: {
                  maxHeight: 200,
                },
              }}
            />
            <Stack direction="row">
              <SearchBar
                onChange={setFilterText}
                onClear={handleClear}
                searchText={filterText}
              />
            </Stack>
          </Stack>
        }
        defaultSortFieldId={4}
        paginationResetDefaultPage={resetPaginationToggle}
      />
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={showError}
        onClose={() => setShowError(false)}
      >
        <Alert onClose={() => setShowError(false)} severity="error">
          Failed to fetch activity library data.
        </Alert>
      </Snackbar>
    </FlexExpandingWrapper>
  );
};
